import { CommonHelper } from 'src/utils/commonHelper';
import { Delete as DeleteIcon } from '@mui/icons-material';
import BrandBillingSettings from 'src/pages/brand-payment/brand-billing-settings';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import { UserListHead } from 'src/sections/@dashboard/user';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import { useToaster } from 'src/utils/toaster/toasterContext';
import { useTranslation } from 'react-i18next';

const ReSellerOrganizationBrandTable = ({
  allBrands,
  rowsPerPage,
  page,
  handleChangePage,
  handleChangeRowsPerPage,
  handleBrandStatus,
  handleDelete,
  getAllBrands,
}) => {
  const { showToast } = useToaster();
  const { t: translate } = useTranslation();

  const brandList = allBrands;
  const tableRef = useRef(null);
  const paginationRef = useRef(null);
  const [tableHeight, setTableHeight] = useState(320);

  useLayoutEffect(() => {
    const resize = () => {
      if (!tableRef.current) return;
      const top = tableRef.current.getBoundingClientRect().top;
      const footerHeight = paginationRef.current?.getBoundingClientRect().height || 52;
      setTableHeight(Math.max(160, window.innerHeight - top - footerHeight - 24));
    };
    resize();
    const observer = new ResizeObserver(resize);
    if (paginationRef.current) observer.observe(paginationRef.current);
    if (tableRef.current?.parentElement?.parentElement) observer.observe(tableRef.current.parentElement.parentElement);
    window.addEventListener('resize', resize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, []);
  const [billingBrand, setBillingBrand] = useState(null);
  const [editableBrand, setEditablebrand] = useState(null);

  const handleEditLimit = (brand) => {
    setEditablebrand(brand);
  };

  const handleUpdateBrand = () => {
    let props = {
      brand_id: editableBrand.brand_id,
      social_account_limit: editableBrand.brand_limit_info.social_account_limit,
      users_limit: editableBrand.brand_limit_info.users_limit,
      ai_content_tokens: editableBrand.brand_limit_info.ai_content_tokens,
      ai_image_token: editableBrand.brand_limit_info.ai_image_token,
      limit_brands: editableBrand.limit_brands,
    };
    let postProps = {
      url: userModuleURL.limitBrand,
      body: props,
      successCallback: updateBrandLimitSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const handleLimitChange = (newValue, key) => {
    const brandDetails = { ...editableBrand };
    brandDetails.brand_limit_info[key] = parseInt(newValue, 10);
    setEditablebrand(brandDetails);
  };

  const updateBrandLimitSuccessCallback = (data, message) => {
    // setOldBrandData(JSON.parse(JSON.stringify(brandData)));
    message && showToast(message, 's');
    getAllBrands();
  };

  const failureCallback = (message) => {
    // setBrandData(JSON.parse(JSON.stringify(oldBrandData)));
    message && showToast(message, 'e');
  };

  return (
    <>
      {billingBrand && <BrandBillingSettings brand={billingBrand} onClose={() => setBillingBrand(null)} />}
      <TableContainer ref={tableRef} sx={{ maxHeight: tableHeight, overflow: 'auto' }}>
        <Table stickyHeader>
          <UserListHead
            headLabel={[
              { id: 'profile', label: translate('reseller_org_brand_table.profile', 'Profile'), alignCenter: false },
              {
                id: 'brand name',
                label: translate('reseller_org_brand_table.brand_name', 'Brand Name'),
                alignRight: false,
              },
              { id: 'status', label: translate('reseller_org_brand_table.status', 'Status'), alignCenter: true },
              {
                id: 'no_of_social_accounts',
                label: translate('reseller_org_brand_table.social_used', 'Social Used'),
                alignCenter: false,
              },
              {
                id: 'no_of_social_accounts_limit',
                label: translate('reseller_org_brand_table.social_limit', 'Social Limit'),
                alignCenter: false,
              },
              {
                id: 'no_of_users_limit',
                label: translate('reseller_org_brand_table.users_limit', 'Users Limit'),
                alignCenter: false,
              },
              {
                id: 'no_of_ai_tokens',
                label: translate('reseller_org_brand_table.ai_token_available', 'AI Token  (Available)'),
                alignCenter: false,
              },
              {
                id: 'no_of_ai_image_tokens',
                label: translate('reseller_org_brand_table.ai_image_available', 'AI Image (Available)'),
                alignCenter: false,
              },
              {
                id: 'edit_limit',
                label: translate('reseller_org_brand_table.edit_limit', 'Edit Limit'),
                alignCenter: true,
              },
              { id: 'delete', label: translate('reseller_org_brand_table.delete', 'Delete'), alignCenter: true },
            ]}
            rowCount={brandList.length}
            isShowCheckbox={false}
          />
          <TableBody>
            {brandList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((brand, index) => (
              <TableRow
                hover
                key={brand.brand_id}
                sx={{
                  backgroundColor: editableBrand?.brand_id === brand.brand_id ? 'rgba(255, 243, 205, 0.6)' : 'inherit',
                  transition: 'background-color 0.3s',
                }}
                tabIndex={-1}
              >
                <TableCell component="th" scope="row" sx={{ py: 3 }}>
                  <Avatar
                    src={brand.brand_image ? brand.brand_image : undefined}
                    alt={brand.brand_name ? brand.brand_name.charAt(0).toUpperCase() : brand.brand_id}
                    sx={{ height: 40, width: 40, bgcolor: '#00AB55', color: '#ffffff' }}
                  >
                    {!brand.brand_image && brand.brand_name?.charAt(0).toUpperCase()}
                  </Avatar>
                </TableCell>

                <TableCell align="left">{brand.brand_name}</TableCell>

                <TableCell align="center">
                  <StyledComponent.SwitchIcon
                    sx={{ m: 2 }}
                    checked={brand.is_enabled}
                    onClick={() => handleBrandStatus(brand, index)}
                  />
                </TableCell>
                <TableCell align="left">{brand.brand_limit_used.social_account_limit}</TableCell>
                <TableCell component="th" scope="row" sx={{ py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {editableBrand?.brand_id === brand.brand_id ? (
                      <TextField
                        type="number"
                        inputProps={{ min: 0 }}
                        value={brand.brand_limit_info.social_account_limit}
                        onChange={(e) => handleLimitChange(e.target.value, 'social_account_limit')}
                        size="small"
                        sx={{ width: 60 }}
                        variant="outlined"
                      />
                    ) : (
                      <>
                        <Typography color={brand.limit_brands ? 'text.primary' : 'text.disabled'} variant="body1">
                          {brand.brand_limit_info.social_account_limit}
                        </Typography>
                      </>
                    )}
                  </Box>
                </TableCell>

                <TableCell component="th" scope="row" sx={{ py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {editableBrand?.brand_id === brand.brand_id ? (
                      <TextField
                        type="number"
                        inputProps={{ min: 0 }}
                        value={brand.brand_limit_info.users_limit}
                        onChange={(e) => handleLimitChange(e.target.value, 'users_limit')}
                        size="small"
                        sx={{ width: 60 }}
                        variant="outlined"
                      />
                    ) : (
                      <>
                        <Typography color={brand.limit_brands ? 'text.primary' : 'text.disabled'} variant="body1">
                          {brand.brand_limit_info.users_limit}
                        </Typography>
                      </>
                    )}
                  </Box>
                </TableCell>

                <TableCell component="th" scope="row" sx={{ py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {editableBrand?.brand_id === brand.brand_id ? (
                      <TextField
                        type="number"
                        inputProps={{ min: 0 }}
                        value={brand.brand_limit_info.ai_content_tokens / 100}
                        onChange={(e) =>
                          handleLimitChange(Math.round(Number(e.target.value) * 100), 'ai_content_tokens')
                        }
                        size="small"
                        sx={{ width: 60 }}
                        variant="outlined"
                      />
                    ) : (
                      <>
                        <Typography color={brand.limit_brands ? 'text.primary' : 'text.disabled'} variant="body1">
                          {brand.brand_limit_info.ai_content_tokens / 100}
                        </Typography>
                      </>
                    )}
                  </Box>
                </TableCell>

                <TableCell component="th" scope="row" sx={{ py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {editableBrand?.brand_id === brand.brand_id ? (
                      <TextField
                        type="number"
                        inputProps={{ min: 0 }}
                        value={brand.brand_limit_info.ai_image_token}
                        onChange={(e) => handleLimitChange(e.target.value, 'ai_image_token')}
                        size="small"
                        sx={{ width: 60 }}
                        variant="outlined"
                      />
                    ) : (
                      <>
                        <Typography color={brand.limit_brands ? 'text.primary' : 'text.disabled'} variant="body1">
                          {brand.brand_limit_info.ai_image_token}
                        </Typography>
                      </>
                    )}
                  </Box>
                </TableCell>

                <TableCell align="center" sx={{ py: 3 }}>
                  {CommonHelper.GetBrandBillingAccess(brand.brand_id).canManage && (
                    <Button size="small" onClick={() => setBillingBrand(brand)}>
                      {translate('billing_payout.billing')}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => {
                      editableBrand?.brand_id === brand.brand_id ? handleUpdateBrand() : handleEditLimit(brand);
                    }}
                  >
                    {editableBrand?.brand_id === brand.brand_id
                      ? translate('reseller_org_brand_table.update_limit', 'Update Limit')
                      : translate('reseller_org_brand_table.edit', 'Edit')}
                  </Button>
                </TableCell>

                <TableCell align="center">
                  <IconButton size="large" onClick={() => handleDelete(brand.brand_id)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        ref={paginationRef}
        sx={{ flexShrink: 0, bgcolor: 'background.paper' }}
        component="div"
        rowsPerPageOptions={[10, 15, 25]}
        count={brandList.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
};

export default ReSellerOrganizationBrandTable;
